/**
 * StackedToaster — card-deck toast host.
 *
 * Replaces react-hot-toast's flat <Toaster> list, which fills the screen
 * corner when many actions fire at once (10–20 enrichments). Keeps
 * react-hot-toast as the engine (timers, dedupe, dismiss) but renders a deck:
 *
 *   ┌────────────────────────┐
 *   │  newest toast (card)   │  ← fully visible, on top
 *   └────────────────────────┘
 *     ────────────────────      ← subtle edges peeking underneath
 *       ( +5 more )             ← click/hover expands the full list
 *
 * Collapsed by default; expands to the full vertical list on hover (desktop)
 * or pill click (touch). Timers pause while expanded, matching the original
 * Toaster's hover behaviour.
 *
 * ⚠️ SINGLE-SUBSCRIBER RULE: only <StackedToaster> calls useToaster().
 * react-hot-toast 2.6 keeps module-level listeners per toasterId and its
 * cleanup splices BY KEY MATCH, NOT IDENTITY — two components subscribed
 * under the same default key (e.g. host + per-position deck) let one remount
 * (HMR, StrictMode double-invoke) splice out the OTHER's listener. Symptom:
 * toasts fire into the store but the visible deck never updates — a toast
 * stuck on screen forever, dismiss no-ops. Decks below are purely
 * presentational and receive their slice as props.
 */

import { useState } from 'react';
import toast, { ToastIcon, resolveValue, useToaster, type Toast } from 'react-hot-toast';
import { TOAST_BASE_STYLE, TOAST_POSITION } from '../../../utils/toast';

const MAX_EDGES = 2; // decorative strips peeking under the top card
const GUTTER = 8;

/** Anchor geometry per position group. Only top positions exist in this app. */
const anchorFor = (position: string): React.CSSProperties => {
  if (position === 'top-center') {
    return { top: 16, left: '50%', transform: 'translateX(-50%)' };
  }
  return { top: 16, right: 16 }; // top-right (default) — also hosts bottom-* (none in use)
};

/** Built-in card for success/error/blank toasts (admin screens call these raw). */
const BuiltInCard = ({ t }: { t: Toast }) => (
  <div
    onClick={() => toast.dismiss(t.id)}
    style={{
      ...TOAST_BASE_STYLE,
      padding: '10px 14px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      maxWidth: '360px',
      cursor: 'pointer',
      pointerEvents: 'auto',
      opacity: t.visible ? 1 : 0,
      transform: t.visible ? 'translateX(0) scale(1)' : 'translateX(16px) scale(0.97)',
      transition: 'transform 380ms cubic-bezier(0.21, 1.02, 0.73, 1), opacity 280ms ease',
    }}
  >
    <ToastIcon toast={t} />
    <span>{resolveValue(t.message, t)}</span>
  </div>
);

/** Decorative strip suggesting more cards underneath. */
const Edge = ({ index }: { index: number }) => (
  <div
    aria-hidden
    style={{
      position: 'absolute',
      left: '50%',
      top: `calc(100% - ${8 - index * 7}px)`,
      width: `calc(100% - ${16 + index * 16}px)`,
      height: '12px',
      transform: 'translateX(-50%)',
      background: 'rgba(27, 27, 31, 0.85)',
      border: '1px solid rgba(255, 255, 255, 0.06)',
      borderRadius: '12px',
      zIndex: 1,
      pointerEvents: 'none',
    }}
  />
);

const pillStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 10px)',
  right: 0,
  background: 'rgba(27, 27, 31, 0.9)',
  color: '#9ca3af',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: '999px',
  padding: '2px 10px',
  fontSize: '11px',
  fontWeight: 600,
  cursor: 'pointer',
  zIndex: 3,
};

interface DeckProps {
  /** This position group's slice of the store (newest first). */
  toasts: Toast[];
  positionKey: string;
  /** Store-level pause handlers from the single useToaster() subscription. */
  startPause: () => void;
  endPause: () => void;
}

/**
 * Presentational deck for one toast position. Custom toasts carry their own
 * click-to-dismiss (renderMinimalToast) and built-ins dismiss via
 * BuiltInCard — no extra click layer here.
 */
const Deck = ({ toasts, positionKey, startPause, endPause }: DeckProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const top = toasts[0];
  const hidden = toasts.slice(1).filter((t) => t.visible);

  if (!top) return null;
  const anchor = anchorFor(top.position || positionKey);

  return (
    <div
      data-stacked-deck={positionKey}
      style={{ position: 'fixed', zIndex: 9999, pointerEvents: 'auto', maxWidth: 'calc(100vw - 32px)', ...anchor }}
      onMouseEnter={() => { startPause(); setIsExpanded(true); }}
      onMouseLeave={() => { endPause(); setIsExpanded(false); }}
    >
      {isExpanded ? (
        // Expanded: full vertical list, newest on top, normal flow.
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: GUTTER }}>
          {toasts.map((t) => (
            <div key={t.id} className="stacked-toast-item">
              {t.type === 'custom' ? resolveValue(t.message, t) : <BuiltInCard t={t} />}
            </div>
          ))}
        </div>
      ) : (
        // Collapsed: newest card + up to MAX_EDGES decorative edges + "+N" pill.
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'relative', zIndex: 2 }}>
            {top.type === 'custom' ? resolveValue(top.message, top) : <BuiltInCard t={top} />}
          </div>
          {hidden.slice(0, MAX_EDGES).map((t, i) => (
            <Edge key={t.id} index={i} />
          ))}
          {hidden.length > 0 && (
            <button
              onClick={() => { startPause(); setIsExpanded(true); }}
              aria-label={`Show ${hidden.length} more notifications`}
              style={pillStyle}
            >
              +{hidden.length}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

interface StackedToasterProps {
  /** Fallback position for toasts that don't declare one. */
  position?: typeof TOAST_POSITION;
  /** Shared base style applied to built-in toast cards (success/error/…). */
  toastOptions?: { style?: React.CSSProperties };
}

/**
 * The app's ONLY useToaster() subscriber for the default toasterId — see the
 * single-subscriber rule above. Groups the live store by declared position
 * and hands each deck its slice as plain props.
 */
export const StackedToaster = ({ position = TOAST_POSITION, toastOptions }: StackedToasterProps) => {
  const { toasts, handlers } = useToaster(toastOptions);
  const { startPause, endPause } = handlers;

  // TEMP diagnostics — transition history for E2E verification
  if (typeof window !== 'undefined') {
    (window as any).__deckState = toasts.map((t) => ({
      id: t.id.slice(-6),
      visible: t.visible,
      dismissed: t.dismissed,
      position: t.position,
    }));
    const log = (window as any).__deckLog = (window as any).__deckLog || [];
    const sig = toasts.map((t) => `${t.id.slice(-4)}:${t.visible ? 'v' : 'h'}`).join(',');
    if (log[log.length - 1] !== sig) log.push(sig);
  }

  // Group by declared position so a stray differently-positioned toast still
  // lands in its own deck instead of corrupting the main one.
  const groups = new Map<string, Toast[]>();
  for (const t of toasts) {
    const key = t.position || position;
    const group = groups.get(key) || [];
    group.push(t);
    groups.set(key, group);
  }

  return (
    <>
      {/* Expand/collapse micro-animations for expanded list items */}
      <style>{`
        @keyframes stacked-toast-in {
          from { opacity: 0; transform: translateX(24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .stacked-toast-item { animation: stacked-toast-in 240ms cubic-bezier(0.21, 1.02, 0.73, 1); }
      `}</style>
      {[...groups.entries()].map(([key, group]) => (
        <Deck
          key={key}
          toasts={group}
          positionKey={key}
          startPause={startPause}
          endPause={endPause}
        />
      ))}
    </>
  );
};

export default StackedToaster;
